'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var TokenBuildersClient = require('./TokenBuildersClient.cjs');
var TokenPdasClient = require('./TokenPdasClient.cjs');
var findMintByAddress = require('./operations/findMintByAddress.cjs');
var findTokenByAddress = require('./operations/findTokenByAddress.cjs');
var findTokenWithMintByAddress = require('./operations/findTokenWithMintByAddress.cjs');
var findTokenWithMintByMint = require('./operations/findTokenWithMintByMint.cjs');
var createMint = require('./operations/createMint.cjs');
var createToken = require('./operations/createToken.cjs');
var createTokenWithMint = require('./operations/createTokenWithMint.cjs');
var mintTokens = require('./operations/mintTokens.cjs');
var sendTokens = require('./operations/sendTokens.cjs');
var freezeTokens = require('./operations/freezeTokens.cjs');
var thawTokens = require('./operations/thawTokens.cjs');
var approveTokenDelegateAuthority = require('./operations/approveTokenDelegateAuthority.cjs');
var revokeTokenDelegateAuthority = require('./operations/revokeTokenDelegateAuthority.cjs');

/**
 * This is a client for the Token module.
 *
 * It enables us to interact with the SPL Token program in order to
 * create, send, freeze, thaw, and mint tokens.
 *
 * You may access this client via the `tokens()` method of your `Metaplex` instance.
 *
 * ```ts
 * const tokenClient = metaplex.tokens();
 * ```
 *
 * @example
 * You can create a new mint account with an associated token account like so.
 * The owner of this token account will, by default, be the current identity
 * of the metaplex instance.
 *
 * ```ts
 * const { token } = await metaplex.tokens().createTokenWithMint();
 * ```
 *
 * @group Modules
 */
class TokenClient {
  constructor(metaplex) {
    this.metaplex = metaplex;
  }

  /**
   * You may use the `builders()` client to access the
   * underlying Transaction Builders of this module.
   *
   * ```ts
   * const buildersClient = metaplex.tokens().builders();
   * ```
   */
  builders() {
    return new TokenBuildersClient.TokenBuildersClient(this.metaplex);
  }

  /**
   * You may use the `pdas()` client to build PDAs related to this module.
   *
   * ```ts
   * const pdasClient = metaplex.tokens().pdas();
   * ```
   */
  pdas() {
    return new TokenPdasClient.TokenPdasClient(this.metaplex);
  }

  // -----------------
  // Queries
  // -----------------

  /** {@inheritDoc findMintByAddressOperation} */
  findMintByAddress(input, options) {
    return this.metaplex.operations().execute(findMintByAddress.findMintByAddressOperation(input), options);
  }

  /** {@inheritDoc findTokenByAddressOperation} */
  findTokenByAddress(input, options) {
    return this.metaplex.operations().execute(findTokenByAddress.findTokenByAddressOperation(input), options);
  }

  /** {@inheritDoc findTokenWithMintByAddressOperation} */
  findTokenWithMintByAddress(input, options) {
    return this.metaplex.operations().execute(findTokenWithMintByAddress.findTokenWithMintByAddressOperation(input), options);
  }

  /** {@inheritDoc findTokenWithMintByMintOperation} */
  findTokenWithMintByMint(input, options) {
    return this.metaplex.operations().execute(findTokenWithMintByMint.findTokenWithMintByMintOperation(input), options);
  }

  // -----------------
  // Create
  // -----------------

  /** {@inheritDoc createMintOperation} */
  createMint(input = {}, options) {
    return this.metaplex.operations().execute(createMint.createMintOperation(input), options);
  }

  /**
   * Create a new Token account from the provided input
   * and returns the newly created `Token` model.
   */
  /** {@inheritDoc createTokenOperation} */
  createToken(input, options) {
    return this.metaplex.operations().execute(createToken.createTokenOperation(input), options);
  }

  /** {@inheritDoc createTokenWithMintOperation} */
  createTokenWithMint(input = {}, options) {
    return this.metaplex.operations().execute(createTokenWithMint.createTokenWithMintOperation(input), options);
  }

  // -----------------
  // Update
  // -----------------

  /** {@inheritDoc mintTokensOperation} */
  mint(input, options) {
    return this.metaplex.operations().execute(mintTokens.mintTokensOperation(input), options);
  }

  /** {@inheritDoc sendTokensOperation} */
  send(input, options) {
    return this.metaplex.operations().execute(sendTokens.sendTokensOperation(input), options);
  }

  /** {@inheritDoc freezeTokensOperation} */
  freeze(input, options) {
    return this.metaplex.operations().execute(freezeTokens.freezeTokensOperation(input), options);
  }

  /** {@inheritDoc thawTokensOperation} */
  thaw(input, options) {
    return this.metaplex.operations().execute(thawTokens.thawTokensOperation(input), options);
  }

  // -----------------
  // Delegate
  // -----------------

  /** {@inheritDoc approveTokenDelegateAuthorityOperation} */
  approveDelegateAuthority(input, options) {
    return this.metaplex.operations().execute(approveTokenDelegateAuthority.approveTokenDelegateAuthorityOperation(input), options);
  }

  /** {@inheritDoc revokeTokenDelegateAuthorityOperation} */
  revokeDelegateAuthority(input, options) {
    return this.metaplex.operations().execute(revokeTokenDelegateAuthority.revokeTokenDelegateAuthorityOperation(input), options);
  }
}

exports.TokenClient = TokenClient;
//# sourceMappingURL=TokenClient.cjs.map
